import { useState, useEffect, useCallback, useRef } from 'react';
import { bookingRequestsAPI } from '../../services/api';

const POLL_INTERVAL_MS = 30_000;

/**
 * Pending booking requests: list, count, approve/reject actions.
 * Data-fetching is delegated to bookingRequestsAPI (src/services/api.js).
 */
export function useBookingApprovals({ filter = 'pending', pollPending = false } = {}) {
  const [requests, setRequests] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const intervalRef = useRef(null);

  const loadRequests = useCallback(async () => {
    try {
      setError('');
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setRequests([]);
        setCount(0);
        return;
      }

      const data = await bookingRequestsAPI.list(filter);
      if (!data.success) {
        throw new Error(data?.message || 'Failed to load booking requests');
      }
      const list = data.data || [];
      setRequests(list);
      if (filter === 'pending') {
        setCount(list.length);
      }
    } catch (e) {
      console.error('Failed to load booking requests:', e);
      setRequests([]);
      setError(e.message || 'Failed to load booking requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const approveRequest = useCallback(async (id, paymentInfo = {}) => {
    const data = await bookingRequestsAPI.approve(id, paymentInfo);
    if (!data.success) {
      throw new Error(data?.message || `Approval failed`);
    }
    await loadRequests();
    return data;
  }, [loadRequests]);

  const rejectRequest = useCallback(async (id, reason = '') => {
    const data = await bookingRequestsAPI.reject(id, reason);
    if (!data.success) {
      throw new Error(data?.message || `Reject failed`);
    }
    await loadRequests();
    return data;
  }, [loadRequests]);

  useEffect(() => {
    setLoading(true);
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (!pollPending || filter !== 'pending') return undefined;
    intervalRef.current = setInterval(loadRequests, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [pollPending, filter, loadRequests]);

  return {
    requests,
    pendingRequests: requests,
    count,
    loading,
    error,
    refresh: loadRequests,
    loadRequests,
    approveRequest,
    rejectRequest,
  };
}
